import {
  CustomFieldType,
  type CustomFieldSchema,
} from '@tribeplatform/gql-client/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { CustomFieldSubtype } from '../../CustomField/utils.js'
import { FormLabelPicker } from '../../Form/FormLabelPicker/FormLabelPicker.js'
import { Form } from '../../Form/index.js'
import { T } from '../../i18n/components/T.js'
import { useStaticIntl } from '../../i18n/index.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { GenericSettings } from '../../Views/components/GenericSettings.js'
import { FieldKeyInput } from '../components/FieldKeyInput.js'
import { FieldSettingsSections } from '../components/FieldSettingsSections.js'
import { useFieldSettings } from '../hooks/useFieldSettings.js'
import {
  readOptionsFactory,
  writeOptionsFactory,
  memberGroupsFactory,
} from '../utils/selectOptions.js'
import { setFieldSettings } from '../utils/utils.js'

interface Props {
  field: CustomFieldSchema
  setField: (field: CustomFieldSchema) => void
  postTypeId: string
  isNew?: boolean
  isLocked: boolean
  goBack: () => void
  goToFields?: () => void
}
export const FieldSettings = ({
  field,
  setField,
  postTypeId,
  isNew,
  isLocked,
  goBack,
  goToFields,
}: Props) => {
  const { $t } = useI18n()
  const readOptions = useStaticIntl(readOptionsFactory)
  const writeOptions = useStaticIntl(writeOptionsFactory)
  const memberGroups = useStaticIntl(memberGroupsFactory)
  const { defaultValues, type, subtype, isCreating, isUpdating, handleSubmit } =
    useFieldSettings({ field, isNew, postTypeId, goToFields, goBack })

  return (
    <GenericSettings
      title={
        <T defaultMessage="Field settings" id="Admin.CMSFields.FieldSettings" />
      }
      description={
        <T
          defaultMessage="Configure the field settings"
          id="Admin.CMSFields.AddNewFieldSettingsHelperText"
        />
      }
      goBack={goBack}
    >
      <Form defaultValues={defaultValues} onSubmit={handleSubmit}>
        <FieldSettingsSections>
          <FieldSettingsSections.General>
            <Form.Input
              label={$t({
                defaultMessage: 'Name',
                id: 'Generics.Name.Noun',
              })}
              name="name"
              onChange={e => setField({ ...field, name: e.target.value })}
            />
            <FieldKeyInput isNew={isNew} />
            {type !== CustomFieldType.boolean && (
              <Form.Toggle
                name="required"
                label={$t({
                  defaultMessage: 'Required',
                  id: 'Generics.Required',
                })}
                disabled={isLocked}
              />
            )}
            {type !== CustomFieldType.boolean &&
              type !== CustomFieldType.date &&
              subtype !== CustomFieldSubtype.IMAGE &&
              subtype !== CustomFieldSubtype.IMAGES &&
              subtype !== CustomFieldSubtype.FILE &&
              subtype !== CustomFieldSubtype.FILES &&
              subtype !== CustomFieldSubtype.MEMBERS &&
              subtype !== CustomFieldSubtype.SPACES && (
                <Form.Input
                  label={
                    <T
                      defaultMessage="Placeholder <small>(optional)</small>"
                      id="Admin.CMSFields.Placeholder"
                    />
                  }
                  name="placeholder"
                  onChange={e => {
                    const newField = {
                      ...field,
                      settings: setFieldSettings(
                        'placeholder',
                        e.target.value,
                        field.settings,
                      ),
                    }
                    setField(newField)
                  }}
                />
              )}
            <Form.Input
              label={
                <T
                  defaultMessage="Helper text <small>(optional)</small>"
                  id="Admin.CMSFields.HelperText"
                />
              }
              helperText={
                <T
                  defaultMessage="A helper text explaining the field like this one"
                  id="Admin.CMSFields.HelperText.Sample"
                />
              }
              name="helperText"
              onChange={e => {
                const newField = {
                  ...field,
                  settings: setFieldSettings(
                    'helperText',
                    e.target.value,
                    field.settings,
                  ),
                }
                setField(newField)
              }}
            />
            {type === CustomFieldType.boolean && (
              <>
                <Form.Input
                  name="positiveLabel"
                  label={
                    <T
                      defaultMessage="Positive label <small>(optional)</small>"
                      id="Admin.CMSFields.PositiveLabel"
                    />
                  }
                  helperText={
                    <T
                      defaultMessage="A label shown when the field value is set to true"
                      id="Admin.CMSFields.PositiveLabel.HelperText"
                    />
                  }
                />
                <Form.Input
                  name="negativeLabel"
                  label={
                    <T
                      defaultMessage="Negative label <small>(optional)</small>"
                      id="Admin.CMSFields.NegativeLabel"
                    />
                  }
                  helperText={
                    <T
                      defaultMessage="A label shown when the field value is set to false"
                      id="Admin.CMSFields.NegativeLabel.HelperText"
                    />
                  }
                />
              </>
            )}
            {(subtype === CustomFieldSubtype.MEMBER ||
              subtype === CustomFieldSubtype.MEMBERS) && (
              <Form.Select
                name="memberGroup"
                label={$t({
                  defaultMessage: 'Member group',
                  id: 'Generics.MemberGroup',
                })}
                items={memberGroups}
              />
            )}
            {(subtype === CustomFieldSubtype.SELECT ||
              subtype === CustomFieldSubtype.MULTISELECT) && (
              <FormLabelPicker
                name="options"
                deleteConfirmation={{
                  title: $t({
                    defaultMessage:
                      'Are you sure you want to remove this option?',
                    id: 'Admin.CMSFields.Settings.MultiSelectRemoveOptionConfirmation',
                  }),
                  proceedLabel: 'Remove',
                  danger: true,
                }}
                label={$t({
                  defaultMessage: 'Options',
                  id: 'Generics.Options',
                })}
                buttonLabel={$t({
                  defaultMessage: 'Add',
                  id: 'Generics.Add',
                })}
              />
            )}
          </FieldSettingsSections.General>
          <FieldSettingsSections.Permissions>
            <Form.Select
              label={
                <T
                  defaultMessage="Who can see this field?"
                  id="Admin.CMSFields.ReadPermission"
                />
              }
              name="readPermission"
              items={readOptions}
              disabled={isLocked}
            />
            <Form.Select
              label={
                <T
                  defaultMessage="Who can update this field?"
                  id="Admin.CMSFields.EditPermission"
                />
              }
              name="writePermission"
              items={writeOptions}
              disabled={isLocked}
            />
          </FieldSettingsSections.Permissions>
        </FieldSettingsSections>
        <div className="mt-5">
          <Button type="submit" loading={isCreating || isUpdating} fullWidth>
            {isNew ? (
              <T defaultMessage="Create" id="Generics.Create" />
            ) : (
              <T defaultMessage="Update" id="Generics.Update" />
            )}
          </Button>
        </div>
      </Form>
    </GenericSettings>
  )
}
